// BOT-EDIT: 2025-01-27T15:30:00Z Create secure invoice viewer with access control
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

interface SecureInvoiceViewerProps {
  invoiceId: string;
  cardId: string;
  onClose: () => void;
}

interface Invoice {
  id: string;
  pdfUrl?: string;
  pdfPath?: string;
  pdfFileName?: string;
  createdBy: string;
  cardId: string;
  amount?: number;
  totalAmount?: number;
  status?: string;
  createdAt?: any;
}

const SecureInvoiceViewer: React.FC<SecureInvoiceViewerProps> = ({
  invoiceId,
  cardId,
  onClose
}) => {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  
  const auth = getAuth();
  const currentUser = auth.currentUser;

  // 안전한 번역 함수
  const safeT = (key: string, fallback: string = key) => {
    try {
      const result = t(key);
      return typeof result === 'string' ? result : fallback;
    } catch (error) {
      return fallback;
    }
  };

  // 인보이스 접근 권한 확인 및 데이터 로드
  useEffect(() => {
    const verifyAccessAndLoadInvoice = async () => {
      if (!currentUser?.uid) {
        setError(safeT('invoice.error.unauthorized', '로그인이 필요합니다'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // 1단계: 카드 소유권 확인
        const cardRef = doc(db, 'users', currentUser.uid, 'cards', cardId);
        const cardSnap = await getDoc(cardRef);
        
        if (!cardSnap.exists()) {
          setError(safeT('invoice.error.cardNotFound', '카드를 찾을 수 없습니다'));
          setLoading(false);
          return;
        }

        // 2단계: 인보이스 데이터 로드 및 권한 확인
        const invoiceRef = doc(db, 'users', currentUser.uid, 'cards', cardId, 'invoices', invoiceId);
        const invoiceSnap = await getDoc(invoiceRef);
        
        if (!invoiceSnap.exists()) {
          setError(safeT('invoice.error.invoiceNotFound', '인보이스를 찾을 수 없습니다'));
          setLoading(false);
          return;
        }

        const invoiceData = invoiceSnap.data() as Invoice;
        
        // 3단계: 접근 권한 검증
        if (invoiceData.createdBy !== currentUser.uid || invoiceData.cardId !== cardId) {
          setError(safeT('invoice.error.accessDenied', '접근 권한이 없습니다'));
          setLoading(false);
          return;
        }

        // 4단계: 성공 - 데이터 설정
        setInvoice({ ...invoiceData, id: invoiceId });
        setHasAccess(true);
        
        console.log('✅ [SecureInvoiceViewer] 인보이스 접근 권한 확인 완료:', {
          invoiceId,
          cardId,
          userId: currentUser.uid,
          hasUrl: !!invoiceData.pdfUrl
        });

      } catch (error) {
        console.error('❌ [SecureInvoiceViewer] 인보이스 로드 실패:', error);
        setError(safeT('invoice.error.loadFailed', '인보이스 로드에 실패했습니다'));
      } finally {
        setLoading(false);
      }
    };

    verifyAccessAndLoadInvoice();
  }, [invoiceId, cardId, currentUser?.uid]);

  // 보안 강화된 PDF 열기
  const handleSecureOpenPdf = async () => {
    if (!invoice?.pdfUrl || !hasAccess) {
      alert(safeT('invoice.error.noAccess', 'PDF에 접근할 수 없습니다'));
      return;
    }

    try {
      // 🔐 보안 로깅
      console.log('🔐 [SecureInvoiceViewer] 보안 PDF 접근:', {
        userId: currentUser?.uid,
        invoiceId: invoice.id,
        cardId: cardId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });

      // PDF URL에 추가 검증 토큰 포함 (옵션)
      const secureUrl = `${invoice.pdfUrl}&access_token=${btoa(currentUser!.uid + ':' + Date.now())}`;
      
      // 새 탭에서 열기 (팝업 차단 대응)
      const newWindow = window.open(secureUrl, '_blank', 'noopener,noreferrer');
      
      if (!newWindow) {
        // 팝업 차단된 경우 다운로드 링크 생성
        const link = document.createElement('a');
        link.href = secureUrl;
        link.download = invoice.pdfFileName || `invoice_${invoice.id}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

    } catch (error) {
      console.error('❌ [SecureInvoiceViewer] PDF 열기 실패:', error);
      alert(safeT('invoice.error.openFailed', 'PDF 열기에 실패했습니다'));
    }
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 w-96 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{safeT('invoice.loading', '인보이스를 불러오는 중...')}</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error || !hasAccess) {
    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 w-96 text-center">
          <div className="text-red-500 text-5xl mb-4">🔒</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {safeT('invoice.error.title', '접근 오류')}
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {safeT('common.close', '닫기')}
          </button>
        </div>
      </div>
    );
  }

  // 인보이스 뷰어
  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-[500px] max-w-[90vw]">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            🧾 {safeT('invoice.viewer.title', '인보이스 보기')}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 인보이스 정보 */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="font-medium text-gray-700">{safeT('invoice.id', '인보이스 ID')}:</span>
              <p className="text-gray-900">{invoice?.id}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">{safeT('invoice.amount', '금액')}:</span>
              <p className="text-gray-900">
                {invoice?.totalAmount?.toLocaleString('ko-KR') || invoice?.amount?.toLocaleString('ko-KR') || 'N/A'}원
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">{safeT('invoice.status', '상태')}:</span>
              <p className="text-gray-900">{invoice?.status || 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">{safeT('invoice.created', '생성일')}:</span>
              <p className="text-gray-900">
                {invoice?.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="flex space-x-3">
          <button
            onClick={handleSecureOpenPdf}
            disabled={!invoice?.pdfUrl}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {safeT('invoice.viewPdf', 'PDF 보기')}
          </button>
          
          <button
            onClick={onClose}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
          >
            {safeT('common.close', '닫기')}
          </button>
        </div>

        {/* 보안 안내 */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <p className="text-sm text-blue-800 font-medium">
                {safeT('invoice.security.title', '보안 인증 완료')}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {safeT('invoice.security.desc', '이 인보이스는 보안 검증을 통과했습니다')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecureInvoiceViewer;




